import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocalizedName } from '../../lib/localization'
import { useAuth } from '../../hooks/useAuth'
import { useMemberKind, usePermissions, useScopes } from '../../contexts/PermissionsContext'
import { Skeleton } from '../../components/ui/skeleton'
import { homeForKind } from '../../lib/member-kinds'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { $api } from '../../api/queryClient'
import { AppIcon } from '../../components/ui/icon'
import { Card } from '../../components/ui/card'
import { IconTile } from '../../components/ui/icon-tile'
import { mdiCheckCircle, mdiAlert, mdiAlertCircle, mdiMapMarkerOutline, mdiStar } from '@mdi/js'
import { Badge } from '../../components/ui/badge'
import { groupScopesByAsset, solePlaceUid } from '../../lib/my-places'
import { shellForKind } from '../../lib/sidebar-menus'
import { useFavoriteAssets } from '../../hooks/useFavoriteAssets'
import { useAssetTypes } from '../../hooks/useQueries'
import { useQueries } from '@tanstack/react-query'
import { apiClient } from '../../api/client'
import { CategoryCards } from '../../components/steering/CategoryCards'
import { CriticalAssetsPanel } from '../../components/home/CriticalAssetsPanel'
import { AssetTypeTile } from '../../components/assets/AssetTypeTile'
import { STEERING_CATEGORIES, type SteeringCategory } from '../../components/steering/steering-categories'
import { PageHeader } from '../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { useCan } from '../../contexts/PermissionsContext'
import { EmptyState } from '../../components/ui/empty-state'

type HealthStatus = 'clear' | 'warning' | 'critical'

/** Same default as Steering's queue, so the cards here and the list there
 * count the same set. */
const ACTIVE_STATUS = 'draft,open,in_progress'

/** Steering's three. Data quality is not this page's business. */
const STEERING_CATEGORY_FILTER = STEERING_CATEGORIES.join(',')

/** {category: count} from a group_by=category stats response. */
function toCategoryCounts(rows: Array<{ category?: string | null; count?: number }> | undefined): Record<string, number> {
    const out: Record<string, number> = {}
    for (const r of rows ?? []) {
        if (typeof r.category === 'string' && typeof r.count === 'number') out[r.category] = r.count
    }
    return out
}

function deriveHealthStatus(statsData: { priority?: number | null; count?: number }[]): { status: HealthStatus; total: number; criticalCount: number; warningCount: number } {
    const criticalCount = statsData
        .filter(s => s.priority != null && s.priority <= 2)
        .reduce((sum, s) => sum + (s.count ?? 0), 0)
    const warningCount = statsData
        .filter(s => s.priority === 3)
        .reduce((sum, s) => sum + (s.count ?? 0), 0)
    const total = statsData.reduce((sum, s) => sum + (s.count ?? 0), 0)

    if (criticalCount > 0) return { status: 'critical', total, criticalCount, warningCount }
    if (warningCount > 0 || total > 0) return { status: 'warning', total, criticalCount, warningCount }
    return { status: 'clear', total, criticalCount, warningCount }
}

export const Route = createFileRoute('/$org/')({
    component: Index,
})

function Index() {
    const { isAuthenticated } = useAuth()
    const navigate = useNavigate()
    const { org } = Route.useParams()
    const { t } = useTranslation('issues')

    // A resident's home is "My places", and so is the root of their
    // breadcrumbs everywhere else (lib/sidebar-menus.ts).
    const memberKind = useMemberKind()
    useBreadcrumbs([
        { title: shellForKind(memberKind).myPlacesBreadcrumb ? t('common:nav.my_places') : t('common:nav.home') }
    ])

    useEffect(() => {
        if (isAuthenticated === false) {
            const currentPath = window.location.pathname + window.location.search
            navigate({
                to: '/$org/login',
                params: { org },
                search: { next: currentPath, email: undefined, password: undefined }
            }).catch((error) => {
                console.error('Navigation error:', error)
            })
        }
    }, [isAuthenticated, navigate, org])

    // Residents and service providers get "My places" instead of the manager
    // dashboard (spec 2026-09-04-03, frontend change 5): the health signal and
    // steering queues are about a portfolio they do not have; their anchors
    // are. The membership kind decides it (spec 2026-10-06-04), so a staff
    // member narrowed by grants, such as an agency director, keeps the manager
    // home. While the kind is unknown nothing is rendered but a placeholder:
    // mounting the manager home would flash it to a resident and fire its
    // requests.
    const { isLoading: membershipLoading } = usePermissions()
    const home = homeForKind(memberKind, membershipLoading)

    if (!isAuthenticated) {
        return null
    }
    if (home === 'loading') {
        return <HomeLoading />
    }
    if (home === 'my-places') {
        return <MyPlacesHome org={org} />
    }

    return <ManagerHome org={org} />
}

function HomeLoading() {
    return (
        <div data-testid="home-loading" aria-busy="true" className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
        </div>
    )
}

function MyPlacesHome({ org }: { org: string }) {
    const { data: assetTypes = [] } = useAssetTypes(org)

    return <MyPlaces org={org} assetTypes={assetTypes} />
}

function ManagerHome({ org }: { org: string }) {
    const navigate = useNavigate()
    const { t } = useTranslation('issues')

    // Issues stats for health signal.
    const { data: issueStatsData, isLoading: issueStatsLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/stats',
        {
            params: {
                path: { orgCode: org },
                // Scoped to the three categories like everything else on this
                // page: counting data-quality tickets here would quote a figure
                // for work that is not shown anywhere below.
                query: { metric: 'count', group_by: 'priority', status: ACTIVE_STATUS, category: STEERING_CATEGORY_FILTER, assignee_uid: 'all' },
            },
        }
    )

    // The three category counts, server-side — the same numbers Steering
    // shows, so the two pages cannot disagree about how much work there is.
    const { data: categoryStatsData, isLoading: categoryStatsLoading } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/stats',
        {
            params: {
                path: { orgCode: org },
                query: { group_by: 'category', status: ACTIVE_STATUS, assignee_uid: 'all' },
            },
        }
    )
    const { data: urgentStatsData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues/stats',
        {
            params: {
                path: { orgCode: org },
                query: { group_by: 'category', status: ACTIVE_STATUS, priority: '1,2', assignee_uid: 'all' },
            },
        }
    )

    // Favorite assets
    const { favorites, isLoading: favoritesLoading } = useFavoriteAssets(org)
    const { data: assetTypes = [] } = useAssetTypes(org)

    const favoriteAssetQueries = useQueries({
        queries: favorites.map(uid => ({
            queryKey: ['asset', org, uid],
            queryFn: async () => {
                const response = await apiClient.GET('/api/v1/organizations/{orgCode}/assets/{assetUid}', {
                    params: { path: { orgCode: org, assetUid: uid }, query: { include: ['external_ids'] } }
                })
                return response.data
            },
            enabled: !!uid,
            staleTime: 5 * 60 * 1000,
        })),
    })
    const favoriteAssets = favoriteAssetQueries
        .map(q => q.data)
        .filter(Boolean)

    // Derive health status from stats
    const healthInfo = useMemo(() => {
        if (!issueStatsData?.data) return { status: 'clear' as HealthStatus, total: 0, criticalCount: 0, warningCount: 0 }
        return deriveHealthStatus(issueStatsData.data)
    }, [issueStatsData])

    const countsByCategory = useMemo(() => toCategoryCounts(categoryStatsData?.data), [categoryStatsData])
    const urgentByCategory = useMemo(() => toCategoryCounts(urgentStatsData?.data), [urgentStatsData])

    const canGroupSteering = useCan('group_steering.read')
    const { t: tGroup } = useTranslation('group-steering')

    const goToSteering = (category: SteeringCategory) => {
        navigate({
            to: '/$org/steering',
            params: { org },
            search: {
                category, asset: undefined, view: undefined, selected: undefined,
                subcategory: undefined, subtree: undefined, due: undefined, status: undefined, sort: undefined,
                q: undefined, page: undefined, tab: undefined, nature: undefined,
            },
        }).catch((error) => console.error('Navigation error:', error))
    }

    const scrollToActions = () => {
        document.getElementById('action-required')?.scrollIntoView({ behavior: 'smooth' })
    }

    return (
        <div>
            <PageHeader
                className="mb-6"
                icon={SECTION_ICONS.home}
                title={t('dashboard.title')}
                description={t('dashboard.description')}
                data-testid="home-title"
            />

            {/* Section 1: Health Signal Banner */}
            <HealthSignalBanner
                healthInfo={healthInfo}
                loading={issueStatsLoading}
                onClick={scrollToActions}
            />

            {/* A staff member narrowed by grants (an agency director on one
                agency) lands on their anchors from here: everything else on
                this page is already computed on their perimeter by the API
                (spec 2026-10-10-04). Nothing for an organization-wide member. */}
            <PerimeterPanel org={org} assetTypes={assetTypes} />

            {/* The group console is a space of its own, entered from here,
                the main menu and the organization selector for whoever holds
                group_steering.read (spec 2026-09-11-01). */}
            {canGroupSteering && (
                <Link
                    to="/$org/group"
                    params={{ org }}
                    search={{ period: undefined, level: undefined, family: undefined, kpi: undefined, selected: undefined, q: undefined }}
                    className="mb-4 flex items-center gap-3 rounded-xl border bg-card shadow-card p-3 motion-safe:transition motion-safe:duration-150 hover:-translate-y-px hover:shadow-popover"
                    data-testid="home-group-steering"
                >
                    <IconTile path={SECTION_ICONS.group_steering} gradient="brand" size="md" />
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">{tGroup('space.title')}</div>
                        <div className="text-xs text-muted-foreground truncate">{tGroup('consolidated.intro')}</div>
                    </div>
                </Link>
            )}

            {/* The same three blocks as Steering, with the same server counts.
                Home answers "how much, and of what kind" and hands over; the
                queue itself lives in Steering. Before, home reimplemented that
                queue — a second, weaker one whose tabs counted the 20 rows it
                had received rather than the thousands that existed, right under
                a banner quoting the true figure (spec 2026-08-24-03, lot 11). */}
            <CategoryCards
                counts={countsByCategory}
                urgentCounts={urgentByCategory}
                loading={categoryStatsLoading}
                onSelect={goToSteering}
            />

            {/* Favorite Assets */}
            {!favoritesLoading && favorites.length > 0 && (
                <div className="mb-6">
                    <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                        {/* The warm accent's first use: the star is the one
                            ornament on the page, and it is not a status. */}
                        <AppIcon path={mdiStar} size="md" className="text-accent-warm" />
                        {t('common:nav.favorites', 'Favorites')}
                    </h2>
                    <div className="flex flex-wrap gap-2">
                        {favoriteAssets.map((asset: any) => {
                            const assetType = assetTypes.find((at: any) => at.uid === asset.asset_type_uid)
                            const name = asset.name || asset.external_ids?.code || asset.uid
                            return (
                                <Link
                                    key={asset.uid}
                                    to="/$org/assets/$assetUid"
                                    params={{ org, assetUid: asset.uid }}
                                    className="flex items-center gap-2 rounded-lg border bg-card shadow-card px-3 py-2 text-sm font-medium text-foreground motion-safe:transition motion-safe:duration-150 hover:-translate-y-px hover:shadow-popover"
                                    data-testid={`favorite-asset-${asset.uid}`}
                                >
                                    <AssetTypeTile type={assetType} asset={asset} size="sm" />
                                    <span>{name}</span>
                                </Link>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Where the work is, which Steering cannot answer: it makes you
                pick a category first, so nobody ever sees the cross-category
                picture. Ranked by urgency, each row split across the same three
                categories, each linking into Steering scoped to that branch. */}
            <CriticalAssetsPanel org={org} status={ACTIVE_STATUS} />

        </div>
    )
}

// Health Signal Banner component
function HealthSignalBanner({ healthInfo, loading, onClick }: {
    healthInfo: { status: HealthStatus; total: number; criticalCount: number; warningCount: number }
    loading: boolean
    onClick: () => void
}) {
    const { t } = useTranslation('issues')

    if (loading) {
        return (
            <div className="mb-6 rounded-xl border bg-muted/50 p-4 animate-pulse" data-testid="health-signal-banner">
                <div className="h-6 w-64 bg-muted rounded"></div>
            </div>
        )
    }

    // The semantic tint is the fill AND the border — no accent rail. Semantic
    // states stay flat: the gradients mark identity, never status
    // (spec 2026-09-17-05).
    const config = {
        clear: {
            bg: 'bg-success/10 border-success/25 hover:bg-success/15',
            icon: mdiCheckCircle,
            iconColor: 'text-success',
            message: t('dashboard.health_clear'),
        },
        warning: {
            bg: 'bg-warning/10 border-warning/25 hover:bg-warning/15',
            icon: mdiAlert,
            iconColor: 'text-warning',
            message: t('dashboard.health_warning', { count: healthInfo.total }),
        },
        critical: {
            bg: 'bg-destructive/10 border-destructive/25 hover:bg-destructive/15',
            icon: mdiAlertCircle,
            iconColor: 'text-destructive',
            message: t('dashboard.health_critical', { count: healthInfo.criticalCount }),
        },
    }

    const c = config[healthInfo.status]

    return (
        <button
            data-testid="health-signal-banner"
            onClick={onClick}
            className={`w-full mb-6 rounded-xl border p-4 flex items-center gap-3 transition-colors cursor-pointer text-left ${c.bg}`}
        >
            <AppIcon path={c.icon} size="lg" className={c.iconColor} />
            <span className="text-sm font-medium text-foreground">{c.message}</span>
        </button>
    )
}


/**
 * The anchors of a staff member's asset-scoped grants, as links (spec
 * 2026-10-10-04). Renders nothing without a grant.
 */
function PerimeterPanel({ org, assetTypes }: { org: string; assetTypes: Array<{ code?: string | null; name?: unknown }> }) {
    const { t } = useTranslation('access')
    const scopes = useScopes()
    const places = useMemo(() => groupScopesByAsset(scopes), [scopes])
    const assetTypeByCode = useMemo(
        () => new Map((assetTypes ?? []).map((at) => [at.code, at])),
        [assetTypes]
    )
    if (places.length === 0) {
        return null
    }

    return (
        <section className="mb-6" data-testid="home-perimeter">
            <h2 className="text-sm font-semibold text-foreground">{t('perimeter.title')}</h2>
            <p className="mb-3 text-xs text-muted-foreground">{t('perimeter.description')}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {places.map((place) => (
                    <Card
                        key={place.asset_uid}
                        className="motion-safe:transition motion-safe:duration-150 hover:-translate-y-px hover:shadow-popover"
                    >
                        <Link
                            to="/$org/assets/$assetUid"
                            params={{ org, assetUid: place.asset_uid }}
                            className="flex items-center gap-2 p-3"
                            data-testid={`home-perimeter-${place.asset_uid}`}
                        >
                            <AssetTypeTile
                                type={assetTypeByCode.get(place.asset_type_code) ?? { code: place.asset_type_code }}
                                size="sm"
                            />
                            <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                                {place.asset_name || place.asset_uid}
                            </span>
                        </Link>
                    </Card>
                ))}
            </div>
        </section>
    )
}

/**
 * "My places" — the home of a user who reaches this organization only through
 * asset-scoped grants (spec 2026-09-04-03, frontend change 5).
 *
 * It renders straight from `scopes` in the membership payload: no request of
 * its own, and no attempt to browse the asset tree, which a resident cannot do
 * above their own lot anyway. Breadcrumbs still work from here because the
 * ancestors of their anchor are common parts and therefore readable.
 */
function MyPlaces({ org, assetTypes }: { org: string; assetTypes: Array<{ code?: string | null; name?: unknown }> }) {
    const { t, i18n } = useTranslation('access')
    const locale = i18n.language?.split('-')[0] || 'en'
    const scopes = useScopes()
    const kind = useMemberKind()
    const navigate = useNavigate()

    // One card per asset, not per grant: an owner who lives in their lot has
    // two grants on it (spec 2026-10-07-03).
    const places = useMemo(() => groupScopesByAsset(scopes), [scopes])
    // A resident with a single place goes straight to it. Providers keep the
    // grid: their places are work sites, not a home.
    const soleUid = kind === 'resident' ? solePlaceUid(places) : undefined
    useEffect(() => {
        if (!soleUid) return
        navigate({ to: '/$org/assets/$assetUid', params: { org, assetUid: soleUid }, replace: true })
    }, [soleUid, navigate, org])

    // The scope payload carries a type CODE, not a type. The vocabulary is
    // readable by every member (`organization.read`) and is cached app-wide, so
    // the lookup costs nothing extra; a code with
    // no type still draws its family through the fallback below.
    const assetTypeByCode = useMemo(
        () => new Map((assetTypes ?? []).map((at) => [at.code, at])),
        [assetTypes]
    )

    if (soleUid) {
        return null
    }

    return (
        <div data-testid="my-places">
            <PageHeader
                className="mb-6"
                icon={SECTION_ICONS.home}
                title={t('my_places.title')}
                description={t('my_places.description')}
            />

            {places.length === 0 ? (
                <EmptyState
                    icon={SECTION_ICONS.assets}
                    title={t('my_places.empty')}
                    data-testid="my-places-empty"
                />
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {places.map((place) => (
                        <Card
                            key={place.asset_uid}
                            className="motion-safe:transition motion-safe:duration-150 hover:-translate-y-px hover:shadow-popover"
                        >
                            <Link
                                to="/$org/assets/$assetUid"
                                params={{ org, assetUid: place.asset_uid }}
                                className="block p-4"
                                data-testid={`my-place-${place.asset_uid}`}
                            >
                                <div className="flex items-center gap-2">
                                    <AssetTypeTile
                                        type={assetTypeByCode.get(place.asset_type_code) ?? { code: place.asset_type_code }}
                                        size="sm"
                                    />
                                    <span className="min-w-0 flex-1 font-medium text-foreground truncate">
                                        {place.asset_name || place.asset_uid}
                                    </span>
                                    {place.badges.map((badge) => (
                                        <Badge
                                            key={badge}
                                            variant="secondary"
                                            className="shrink-0"
                                            data-testid={`my-place-badge-${badge}`}
                                        >
                                            {t(`my_places.badge_${badge}`)}
                                        </Badge>
                                    ))}
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                    {/* The type's `name` is the localized map; it
                                        used to be read as `names`, which does
                                        not exist, so the card printed the code. */}
                                    {getLocalizedName(assetTypeByCode.get(place.asset_type_code)?.name as Record<string, string> | undefined, locale) || place.asset_type_code}
                                </div>
                                {place.asset_address && (
                                    <div
                                        className="mt-2 flex items-start gap-1.5 text-sm text-foreground"
                                        data-testid={`my-place-address-${place.asset_uid}`}
                                    >
                                        <AppIcon path={mdiMapMarkerOutline} size="sm" className="mt-0.5 shrink-0 text-muted-foreground" />
                                        <span className="min-w-0">{place.asset_address}</span>
                                    </div>
                                )}
                            </Link>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    )
}
